import restClient from 'restClient'
import { normalizeSuitabilityResultPayload } from './expandVanDerValkSuitabilityResult'
import type { VanDerValkSuitabilityConfig } from './types'

const api = restClient(`${window.API_ROOT}/api`)

type PostError = { ok: false; error: 'parse' | 'http'; message?: string }

type PostSuccess = { ok: true; data: VanDerValkSuitabilityConfig[]; status: number }

export type PostVanDerValkSuitabilityResult = PostSuccess | PostError

/**
 * POST Van der Valk suitability. Routes to flat-roof or pitched-roof endpoint based on mounting type.
 * Pitched-roof sends ``claddingType`` (from roof type); STANDING_SEAM also sends ``seamType``;
 * CORRUGATED also sends top-level ``corrugatedType``, ``substructureMaterial``, and when STEEL
 * ``claddingPitch`` / ``claddingThickness`` (plus nested ``claddingOptions`` when complete).
 */
export async function postVanDerValkSuitability(
  body: {
    width: number
    length: number
    thickness: number
    moduleWeightKg: number
  },
  options?: {
    mountingType?: string
    claddingType?: string
    seamType?: string
    corrugatedType?: string
    substructureMaterial?: string
    claddingPitch?: number
    claddingThickness?: number
    claddingOptions?: Record<string, string | number>
  }
): Promise<PostVanDerValkSuitabilityResult> {
  const project = window.projectForm?.getState?.()?.values
  const orgId = project?.org_id
  const projectId = project?.id
  if (orgId == null || projectId == null) {
    return { ok: false, error: 'http', message: 'missing project context' }
  }

  const isPitched = options?.mountingType === 'onRoof'
  const endpoint = isPitched ? 'pitched-roof' : 'flat-roof'
  const url = `orgs/${orgId}/projects/${projectId}/vandervalk/suitable-systems/${endpoint}/`
  const isCorrugated = options?.claddingType === 'CORRUGATED'
  const data = isPitched
    ? {
        ...body,
        claddingType: options?.claddingType,
        ...(options?.claddingType === 'STANDING_SEAM' && options?.seamType ? { seamType: options.seamType } : {}),
        ...(isCorrugated && options?.corrugatedType ? { corrugatedType: options.corrugatedType } : {}),
        ...(isCorrugated && options?.substructureMaterial
          ? { substructureMaterial: options.substructureMaterial }
          : {}),
        ...(isCorrugated && options?.corrugatedType === 'STEEL' && options?.claddingPitch != null
          ? { claddingPitch: options.claddingPitch }
          : {}),
        ...(isCorrugated && options?.corrugatedType === 'STEEL' && options?.claddingThickness != null
          ? { claddingThickness: options.claddingThickness }
          : {}),
        ...(options?.claddingOptions ? { claddingOptions: options.claddingOptions } : {}),
      }
    : body
  try {
    const response = await api('CUSTOM_POST', 'custom', { url, data })
    const list = normalizeSuitabilityResultPayload(response.data)
    if (list == null) {
      return { ok: false, error: 'parse' }
    }
    return { ok: true, data: list, status: response.status }
  } catch (e) {
    return { ok: false, error: 'http', message: e instanceof Error ? e.message : undefined }
  }
}
